import userCan, { PermissionModel, UserActions } from "@granular/authz";
import {
  useMaybeCurrentOperation,
  useCurrentOperationV4,
} from "./currentOperation";

export const useMaybeUserCan = (
  action: UserActions,
  model: PermissionModel,
): boolean | undefined => {
  const currentOperation = useMaybeCurrentOperation();
  if (currentOperation === undefined || currentOperation.data === undefined) {
    return undefined;
  }
  return userCan(action, model, currentOperation.data);
};

/**
 * Checks if the user can perform the given action on the given authz model.
 *
 * **How to Use:**
 * 1. Import an authorization namespace from the `@granular/authz` package like so:
 *
 * ```
 * import { Field } from "@granular/authz";
 * ```
 *
 * 2. Check if the user has a specific permission:
 *
 * ```
 * const canEditFields = useUserCan("edit", Fields.field);
 * ```
 *
 * 3. Enforce a policy (for example, the Viewer role cannot edit fields):
 *
 * ```
 * <button disabled={!canEditFields}>Edit Field</button>
 * ```
 */
export const useUserCan = (
  action: UserActions,
  model: PermissionModel,
): boolean => {
  const { data: currentOpData } = useCurrentOperationV4();
  return userCan(action, model, currentOpData);
};

export { userCan };
export type { PermissionModel, UserActions };
